import { 
    Alert, 
    Button, 
    CircularProgress,
    Divider,
    FormControlLabel, 
    Grid, 
    LinearProgress,
    Switch, 
    TextField, 
    Typography, 
    styled, 
    Box,
    Collapse,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    alpha
} from '@mui/material';
import AppModal from 'components/AppModal';
import { H2, Tiny } from 'components/Typography';
import { useTranslation } from 'react-i18next';
import { DocumentTypeEntity, NetArrayElementEntity, DryRunResult, FileAnalysisResult } from 'api/generated';
import AppSelectField from 'components/input-fields/MultipleChoiceField';
import { KeyboardArrowDown, CheckCircle, Error, Warning, ExpandMore, ExpandLess, Description, CloudUpload, Assessment } from '@mui/icons-material';
import { FC, useEffect, useState } from 'react';
import { documentTypesApi, idDocumentsApi } from 'api';
import FileUploadInput from 'components/input-fields/FileUploadInput';
import AddPaymentModal, { EmployeeEntityWithAmount } from './dataTableV2/AddPaymentModal';

interface UpdateZipProps {
    open: boolean;
    onClose: () => void;
}

const ModalContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '85vh',
    overflow: 'hidden',
}));

const ScrollableArea = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(3),
    '&::-webkit-scrollbar': {
        width: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: alpha(theme.palette.grey[300], 0.2),
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.primary.main,
        borderRadius: '4px',
        '&:hover': {
            background: theme.palette.primary.dark,
        },
    },
}));

const ModalHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
}));

const ModalFooter = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    borderRadius: theme.spacing(1),
}));

const UploadZipModal: FC<UpdateZipProps> = ({ open, onClose }) => {
    const { t } = useTranslation();
    
    // Form state
    const [documentTypes, setDocumentTypes] = useState<DocumentTypeEntity[]>([]);
    const [selectedType, setSelectedType] = useState<DocumentTypeEntity>();
    const [isVisible, setIsVisible] = useState(true);
    const [prefix, setPrefix] = useState('');
    const [selectedFile, setSelectedFile] = useState<File>();
    const [sendEmail, setSendEmail] = useState(true);
    const [attachFile, setAttachFile] = useState(true);
    const [lookForPayment, setLookForPayment] = useState(false);
    
    // Flow state
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    
    // Result state
    const [uploadResult, setUploadResult] = useState<any>(null);
    const [error, setError] = useState('');
    const [employeesPayment, setEmployeesPayment] = useState<EmployeeEntityWithAmount[]>([]);
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    
    // UI state
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        successful: true,
        failed: false,
    });

    useEffect(() => {
        documentTypesApi.findAll().then(({ data }) => {
            setDocumentTypes(data);
        });
    }, []);

    const resetAll = () => {
        setSelectedType(undefined);
        setIsVisible(true);
        setSelectedFile(undefined);
        setPrefix('');
        setSendEmail(true);
        setAttachFile(true);
        setLookForPayment(false);
        setIsAnalyzing(false);
        setShowPreview(false);
        setDryRunResult(null);
        setIsUploading(false);
        setUploadResult(null);
        setError('');
        setEmployeesPayment([]);
        setExpandedSections({ successful: true, failed: false });
    };

    const analyzeFile = async () => {
        if (!selectedType || !selectedFile) return;

        setIsAnalyzing(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            
            let result: DryRunResult;
            if (prefix === "") {
                result = (await idDocumentsApi.dryRunFileIfTaxCode(
                    selectedType.name, 
                    isVisible, 
                    lookForPayment, 
                    { data: formData }
                )).data;
            } else {
                result = (await idDocumentsApi.dryRunFileIfTaxCodePrefix(
                    selectedType.name, 
                    isVisible, 
                    prefix, 
                    lookForPayment, 
                    { data: formData }
                )).data;
            }

            const successfulFiles = Array.isArray(result.successfulFiles) 
                ? (Array.isArray(result.successfulFiles[0]) 
                    ? result.successfulFiles.flat() 
                    : result.successfulFiles)
                : [];
            const failedFiles = Array.isArray(result.failedFiles) 
                ? (Array.isArray(result.failedFiles[0]) 
                    ? result.failedFiles.flat() 
                    : result.failedFiles)
                : [];

            setDryRunResult({
                ...result,
                successfulFiles: successfulFiles as any,
                failedFiles: failedFiles as any,
            });
            setShowPreview(true);
        } catch (e: any) {
            setError(e.response?.data?.message || e.message || t('uploadZip.analysisError'));
        } finally {
            setIsAnalyzing(false);
        }
    };

    const confirmUpload = async () => {
        if (!selectedType || !selectedFile) return;

        setIsUploading(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            
            let uploadedZip;
            if (prefix === "") {
                uploadedZip = (await idDocumentsApi.createFileIfTaxCode(
                    selectedType.name, 
                    isVisible, 
                    lookForPayment, 
                    sendEmail, 
                    attachFile, 
                    { data: formData }
                )).data;
            } else {
                uploadedZip = (await idDocumentsApi.createFileIfTaxCodePrefix(
                    selectedType.name, 
                    isVisible, 
                    prefix, 
                    lookForPayment, 
                    sendEmail, 
                    attachFile, 
                    { data: formData }
                )).data;
            }

            setUploadResult(uploadedZip);
            setShowPreview(false);
            
            if (uploadedZip.netArray && uploadedZip.netArray.length > 0) {
                setEmployeesPayment(uploadedZip.netArray.map((net: NetArrayElementEntity) => ({
                    amount: net.amount,
                    ...net.employee
                })));
            }
        } catch (e: any) {
            setError(e.response?.data?.message || e.message || t('uploadZip.uploadError'));
        } finally {
            setIsUploading(false);
        }
    };

    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'SUCCESS':
                return <CheckCircle color="success" fontSize="small" />;
            case 'EMPLOYEE_NOT_FOUND':
            case 'TAX_CODE_NOT_FOUND':
                return <Warning color="warning" fontSize="small" />;
            default:
                return <Error color="error" fontSize="small" />;
        }
    };

    const renderForm = () => (
        <>
            <ModalHeader>
                <H2 sx={{ mb: 0.5 }}>{t('common.tables.button.UploadZip')}</H2>
                <Tiny color="text.secondary">{t('uploadZip.description')}</Tiny>
            </ModalHeader>

            <ScrollableArea>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <AppSelectField
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            select
                            variant="outlined"
                            label={t('employees.id.documentTypes.default')}
                            SelectProps={{
                                native: true,
                                IconComponent: KeyboardArrowDown,
                            }}
                            value={selectedType?.name || ''}
                            onChange={(e) => {
                                setSelectedType(documentTypes.find((type) => {
                                    return type.name === e.target.value;
                                }));
                            }}
                        >
                            <option value="">{t('employees.id.documentTypes.default')}</option>
                            {documentTypes.map((documentType) => (
                                <option key={documentType.id} value={documentType.name}>
                                    {documentType.name}
                                </option>
                            ))}
                        </AppSelectField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t('uploadZip.prefix')}
                            value={prefix}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setPrefix(event.target.value);
                            }}
                            helperText={t('uploadZip.prefixHelper')}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('uploadZip.selectFile')}
                            </Typography>
                            <FileUploadInput
                                handleOnChange={(e) => {
                                    if (e.target.files) {
                                        setSelectedFile(Array.from(e.target.files)[0]);
                                    }
                                }}
                                resetOnChange={false}
                                disabled={false}
                                multiple={false}
                            />
                            {selectedFile && (
                                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircle color="success" fontSize="small" />
                                    <Typography variant="body2">{selectedFile.name}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={isVisible} 
                                    onChange={(e) => {
                                        setIsVisible(e.target.checked);
                                        if (!e.target.checked) {
                                            setSendEmail(false);
                                            setAttachFile(false);
                                        }
                                    }} 
                                    color="primary"
                                />
                            }
                            label={t('employees.id.documentTypes.isVisible')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={lookForPayment} 
                                    onChange={(e) => setLookForPayment(e.target.checked)} 
                                    color="primary"
                                />
                            }
                            label={t('uploadZip.lookForPayment')}
                        />
                    </Grid>

                    {isVisible && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={sendEmail}
                                            onChange={() => {
                                                setSendEmail(!sendEmail);
                                                if (!sendEmail) {
                                                    setAttachFile(false);
                                                }
                                            }}
                                            color="primary"
                                        />
                                    }
                                    label={t('employees.document.sendEmail')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={attachFile}
                                            onChange={() => setAttachFile(!attachFile)}
                                            disabled={!sendEmail}
                                            color="primary"
                                        />
                                    }
                                    label={t('employees.document.attachFile')}
                                />
                            </Grid>
                        </>
                    )}

                    {isAnalyzing && (
                        <Grid item xs={12}>
                            <Box sx={{ mt: 1 }}>
                                <LinearProgress sx={{ mb: 1 }} />
                                <Typography variant="body2" color="text.secondary" align="center">
                                    {t('uploadZip.analyzingFiles')}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </ScrollableArea>

            <ModalFooter>
                <StyledButton 
                    variant="outlined" 
                    onClick={() => {
                        resetAll();
                        onClose();
                    }}
                >
                    {t('common.forms.button.cancel')}
                </StyledButton>
                <StyledButton
                    variant="contained"
                    disabled={!selectedFile || !selectedType || isAnalyzing}
                    onClick={analyzeFile}
                    startIcon={isAnalyzing ? <CircularProgress size={16} color="inherit" /> : <Assessment />}
                >
                    {isAnalyzing ? t('uploadZip.analyzing') : t('uploadZip.analyze')}
                </StyledButton>
            </ModalFooter>
        </>
    );

    const renderPreview = () => {
        if (!dryRunResult) return null;

        const summary = dryRunResult.summary || {};
        const successfulFilesRaw = dryRunResult.successfulFiles || [];
        const failedFilesRaw = dryRunResult.failedFiles || [];
        
        const successfulFiles: FileAnalysisResult[] = Array.isArray(successfulFilesRaw[0]) 
            ? (successfulFilesRaw as any).flat() 
            : (successfulFilesRaw as any);
        const failedFiles: FileAnalysisResult[] = Array.isArray(failedFilesRaw[0]) 
            ? (failedFilesRaw as any).flat() 
            : (failedFilesRaw as any);

        return (
            <>
                <ModalHeader>
                    <H2 sx={{ mb: 0.5 }}>{t('uploadZip.previewTitle')}</H2>
                    <Tiny color="text.secondary">{t('uploadZip.previewSubtitle')}</Tiny>
                </ModalHeader>

                <ScrollableArea>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                <Typography variant="h4" color="primary" fontWeight="bold">
                                    {summary.totalProcessed || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('uploadZip.totalFiles')}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                <Typography variant="h4" color="success.main" fontWeight="bold">
                                    {summary.successful || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('uploadZip.successful')}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                <Typography variant="h4" color="error.main" fontWeight="bold">
                                    {summary.failed || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('uploadZip.failed')}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                <Typography variant="h4" color="info.main" fontWeight="bold">
                                    {new Set(successfulFiles.map((f: FileAnalysisResult) => f.employee?.id).filter(Boolean)).size}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('uploadZip.affectedEmployees')}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    {summary.totalNetAmount && summary.totalNetAmount > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {t('uploadZip.totalNetAmount')}: €{summary.totalNetAmount.toFixed(2)}
                        </Alert>
                    )}

                    {successfulFiles.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    p: 1,
                                    bgcolor: alpha('#4caf50', 0.1),
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                }}
                                onClick={() => toggleSection('successful')}
                            >
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {t('uploadZip.successfulFiles')} ({successfulFiles.length})
                                </Typography>
                                <IconButton size="small">
                                    {expandedSections.successful ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Box>
                            <Collapse in={expandedSections.successful}>
                                <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, mt: 0.5 }}>
                                    {successfulFiles.map((file: FileAnalysisResult, index: number) => (
                                        <ListItem key={index} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                            <ListItemIcon>{getStatusIcon(file.status || 'SUCCESS')}</ListItemIcon>
                                            <ListItemText 
                                                primary={file.fileName}
                                                secondary={
                                                    <Box>
                                                        {file.employee && (
                                                            <Typography variant="caption" display="block">
                                                                {file.employee.firstName} {file.employee.lastName} ({file.taxCode})
                                                            </Typography>
                                                        )}
                                                        {file.netAmount && (
                                                            <Typography variant="caption" color="success.main" display="block">
                                                                €{file.netAmount.toFixed(2)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        </Box>
                    )}

                    {failedFiles.length > 0 && (
                        <Box>
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    p: 1,
                                    bgcolor: alpha('#f44336', 0.1),
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                }}
                                onClick={() => toggleSection('failed')}
                            >
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {t('uploadZip.failedFiles')} ({failedFiles.length})
                                </Typography>
                                <IconButton size="small">
                                    {expandedSections.failed ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Box>
                            <Collapse in={expandedSections.failed}>
                                <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, mt: 0.5 }}>
                                    {failedFiles.map((file: FileAnalysisResult, index: number) => (
                                        <ListItem key={index} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                            <ListItemIcon>{getStatusIcon(file.status || 'ERROR')}</ListItemIcon>
                                            <ListItemText 
                                                primary={file.fileName}
                                                secondary={file.errorMessage || file.status}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        </Box>
                    )}
                </ScrollableArea>

                <ModalFooter>
                    <StyledButton
                        variant="outlined"
                        onClick={() => {
                            setShowPreview(false);
                            resetAll();
                            onClose();
                        }}
                    >
                        {t('common.forms.button.cancel')}
                    </StyledButton>
                    <StyledButton
                        variant="contained"
                        onClick={confirmUpload}
                        disabled={isUploading || successfulFiles.length === 0}
                        startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
                    >
                        {isUploading ? t('uploadZip.uploading') : t('uploadZip.confirmUpload')}
                    </StyledButton>
                </ModalFooter>
            </>
        );
    };

    const renderResult = () => {
        if (!uploadResult) return null;

        const successful = uploadResult.count || 0;
        const failed = uploadResult.failedFiles?.length || 0;
        const badFiles = uploadResult.badFiles || [];
        const badPayments = uploadResult.badPayments || [];

        return (
            <>
                <ModalHeader>
                    <H2 sx={{ mb: 0.5 }}>{t('uploadZip.completed')}</H2>
                    <Tiny color="text.secondary">
                        {successful > 0 ? t('uploadZip.filesUploadedSuccessfully', { count: successful }) : t('uploadZip.uploadCompleted')}
                    </Tiny>
                </ModalHeader>

                <ScrollableArea>
                    {successful > 0 && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {t('uploadZip.success', { item: successful })}
                        </Alert>
                    )}

                    {failed > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {t('uploadZip.failedCount', { count: failed })}
                        </Alert>
                    )}

                    {badFiles.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                                {t('uploadZip.notAssignedMessage')}
                            </Typography>
                            <List dense>
                                {badFiles.map((file: string, index: number) => (
                                    <ListItem key={index}>
                                        <ListItemIcon><Description fontSize="small" /></ListItemIcon>
                                        <ListItemText primary={file} />
                                    </ListItem>
                                ))}
                            </List>
                        </Alert>
                    )}

                    {badPayments.length > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                                {t('uploadZip.notAssignedPayments')}
                            </Typography>
                            <List dense>
                                {badPayments.map((file: string, index: number) => (
                                    <ListItem key={index}>
                                        <ListItemIcon><Warning fontSize="small" /></ListItemIcon>
                                        <ListItemText primary={file} />
                                    </ListItem>
                                ))}
                            </List>
                        </Alert>
                    )}
                </ScrollableArea>

                <ModalFooter>
                    <StyledButton
                        fullWidth
                        variant="contained"
                        onClick={() => {
                            if (lookForPayment && employeesPayment.length > 0) {
                                onClose();
                                setOpenPaymentModal(true);
                            } else {
                                resetAll();
                                onClose();
                            }
                        }}
                    >
                        {lookForPayment && employeesPayment.length > 0 
                            ? t('uploadZip.goToPayments') 
                            : t('confirm')
                        }
                    </StyledButton>
                </ModalFooter>
            </>
        );
    };

    const renderError = () => (
        <>
            <ModalHeader>
                <H2 sx={{ mb: 0.5, color: 'error.main' }}>{t('alerts.error')}</H2>
            </ModalHeader>
            <ScrollableArea>
                <Alert severity="error">{error}</Alert>
            </ScrollableArea>
            <ModalFooter>
                <StyledButton
                    fullWidth
                    variant="contained"
                    onClick={() => {
                        resetAll();
                        onClose();
                    }}
                >
                    {t('confirm')}
                </StyledButton>
            </ModalFooter>
        </>
    );

    const getContent = () => {
        if (error && !showPreview && !uploadResult) return renderError();
        if (uploadResult) return renderResult();
        if (showPreview) return renderPreview();
        return renderForm();
    };

    return (
        <>
            <AddPaymentModal
                open={openPaymentModal}
                onClose={() => {
                    setOpenPaymentModal(false);
                    resetAll();
                }}
                setTableData={() => {}}
                tableData={undefined}
                employees={employeesPayment}
            />
            
            <AppModal 
                open={open} 
                handleClose={() => {
                    resetAll();
                    onClose();
                }}
                sx={{ maxWidth: 800 }}
            >
                <ModalContent>
                    {getContent()}
                </ModalContent>
            </AppModal>
        </>
    );
};

export default UploadZipModal;
